import { IANAZone } from "luxon";

import { DateTime } from "../Types/DateTime";
import { CenterDialTime, EnhancedCenterDialTime } from "./CenterDialTime";
import { TimeZone } from "./TimeZone";

export class Center {
  id!: string;
  externalId!: string;
  code!: string;
  name!: string;
  isActive!: boolean;
  isObxDisabled!: boolean;
  timeZone!: TimeZone;
  dialerCount?: number;
  isTextable!: boolean;
  dialTimes!: CenterDialTime[];
  /** A list of the business units supported by the center. */
  businessUnitIds!: string[];
}

/** A Center model from the CRM extended with additional logical props. */
export class EnhancedCenter extends Center {
  /** The luxon time zone offset string. */
  luxonTimeZone: IANAZone;
  dialTimes: EnhancedCenterDialTime[];

  constructor(center: Center) {
    super();

    this.id = center.id;
    this.externalId = center.externalId;
    this.code = center.code;
    this.name = center.name;
    this.isActive = center.isActive;
    this.isObxDisabled = center.isObxDisabled;
    this.timeZone = center.timeZone;
    this.dialerCount = center.dialerCount;
    this.isTextable = center.isTextable;
    this.luxonTimeZone = DateTime.getZone(center.timeZone.currentName);
    this.dialTimes = center.dialTimes.map(d => new EnhancedCenterDialTime(d)).sort(EnhancedCenterDialTime.comparator);
    this.businessUnitIds = center.businessUnitIds;
  }

  /** Truncate extended properties to return the base. */
  static toBase(center: EnhancedCenter): Center {
    const {
      id,
      externalId,
      code,
      name,
      isActive,
      isObxDisabled,
      timeZone,
      dialerCount,
      isTextable,
      dialTimes,
      businessUnitIds
    } = center;
    return {
      id,
      externalId,
      code,
      name,
      isActive,
      isObxDisabled,
      timeZone,
      dialerCount,
      isTextable,
      dialTimes: dialTimes.map(d => EnhancedCenterDialTime.toBase(d)),
      businessUnitIds
    };
  }

  /** Returns `true` if the param centers do not have identical properties, false otherwise. */
  static isChanged = (centerSource: EnhancedCenter, centerComparison: EnhancedCenter): boolean =>
    centerSource.id !== centerComparison.id ||
    centerSource.isActive !== centerComparison.isActive ||
    centerSource.isObxDisabled !== centerComparison.isObxDisabled ||
    centerSource.isTextable !== centerComparison.isTextable ||
    centerSource.dialerCount !== centerComparison.dialerCount ||
    centerSource.dialTimes.length !== centerComparison.dialTimes.length ||
    !centerSource.dialTimes.every(dialTime =>
      centerComparison.dialTimes.some(
        selectedRefDialTime =>
          dialTime.id === selectedRefDialTime.id &&
          dialTime.dialTime === selectedRefDialTime.dialTime &&
          dialTime.isDisabled === selectedRefDialTime.isDisabled
      )
    );

  static hasEnabledDialTime = (center: EnhancedCenter): boolean =>
    Boolean(center.dialerCount) && center.dialTimes && center.dialTimes.some(dialTime => !dialTime.isDisabled);
}
